import { ChangeDetectorRef, Component } from '@angular/core';
import { VendorEmployeeCount } from '@interfaces/vendor-management-model';
import { paginationParams } from '@shared/config/table.config';
import { columnDefs, rowData } from './vendors-list.config';
import { SearchService } from '@services/search.service';
import { ErrorMessages, Months } from '@constants/constants';
import { AuthService } from '@core/auth.service';
import { DataService } from '@services/hiring-manger-profiledata.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  HeaderText,
  PageTitle,
  RTODrill,
  RtoTypeEnum,
} from '@constants/global.enum';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-vendors-list',
  templateUrl: './vendors-list.component.html',
  styleUrl: './vendors-list.component.scss',
})
export class VendorsListComponent {
  displayData: VendorEmployeeCount[] = [];
  vendorEmployeeData: VendorEmployeeCount[] = [];
  paginationParams = paginationParams;
  columnDefs = columnDefs;
  rowData = rowData;
  filterValue: string = '';
  totalCount: number = 0;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;
  isDataLoaded: boolean = true;
  months: string[] = Months;
  monthOptions: {
    value: string;
    label: string;
    month: string;
    year: string;
    selected?: boolean;
  }[] = [];
  selectedMonth: string = '';
  selectedYear: string = new Date().getFullYear().toString();
  leftPercentage: number = 20;
  middlePercentage: number = 60;
  rightPercentage: number = 20;
  employeeId: string | null = null;
  preSelectedValue: string = '';
  preSelectedLabel: string = '';
  pageTitleEnum = PageTitle;
  headerTextEnum = HeaderText;
  RTODrill = RTODrill;
  rto = RtoTypeEnum;
  rtoSegments = [
    { label: 'Regular', key: 'regularPercentage', class: 'segment-left' },
    { label: 'Hybrid', key: 'hybridPercentage', class: 'segment-middle' },
    {
      label: 'Partial Hybrid',
      key: 'partialHybridPercentage',
      class: 'segment-middle1',
    },
    {
      label: 'Remote',
      key: 'remotePercentage',
      class: 'segment-right',
    },
  ];
  private subs = new Subscription();
  showVendorProfile = false;
  selectedVendor: VendorEmployeeCount | null = null;

  constructor(
    private searchService: SearchService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
    private router: Router,
    private toastr: ToastrService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.generateLast12Months();

    const queryParamsSub = this.route.queryParams.subscribe((params) => {
      const monthParam = +params['month'] || 0;
      const yearParam = +params['year'] || 0;
      const hasMonth =
        !Number.isNaN(monthParam) && monthParam >= 1 && monthParam <= 12;
      const hasYear = !Number.isNaN(yearParam) && yearParam > 0;

      if (hasMonth && hasYear) {
        const selectedOption = this.monthOptions.find(
          (option) =>
            Number(option.month) === monthParam &&
            Number(option.year) === yearParam,
        );
        if (selectedOption) {
          this.preSelectedValue = selectedOption.value;
          this.preSelectedLabel = selectedOption.label;
          this.selectedMonth = selectedOption.month;
          this.selectedYear = selectedOption.year;
        } else {
          const preSelectedOption =
            this.monthOptions.find((month) => month?.selected) ??
            this.monthOptions[0];
          if (preSelectedOption) {
            this.preSelectedValue = preSelectedOption.value;
            this.selectedMonth = preSelectedOption.month;
            this.selectedYear = preSelectedOption.year;
          }
        }
        this.authService
          .getEmployeeDetails()
          .then((employeeData) => {
            this.employeeId = employeeData?.employeeId || null;
            if (this.employeeId) {
              this.loadVendorData();
            }
          })
          .catch((error) => {
            console.error('Failed to fetch employee details:', error);
          });
        this.displayData = this.displayData ?? [];
        this.totalCount = this.displayData?.length;
      } else {
        const preSelectedOption =
          this.monthOptions.find((month) => month?.selected) ??
          this.monthOptions[0];
        if (preSelectedOption) {
          this.preSelectedValue = preSelectedOption.value;
          this.selectedMonth = preSelectedOption.month;
          this.selectedYear = preSelectedOption.year;
        }
      }
      this.loadVendorData();
    });
    this.subs.add(queryParamsSub);
  }

  generateLast12Months() {
    const now = new Date();
    this.monthOptions = [];
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
      const monthLabel = monthDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      });
      const monthValue = `${(monthDate.getMonth() + 1).toString().padStart(2, '0')}, ${monthDate.getFullYear()}`;
      this.monthOptions.push({
        label: monthLabel,
        value: monthValue,
        month: (monthDate.getMonth() + 1).toString().padStart(2, '0'),
        year: monthDate.getFullYear().toString(),
        selected: i === 0,
      });
    }
  }

  loadVendorData(): void {
    if (!this.employeeId) return;
    this.dataService
      .getVendorEmployeeCount(
        this.employeeId,
        this.selectedMonth,
        this.selectedYear,
      )
      .subscribe({
        next: (response) => {
            this.vendorEmployeeData = response?.data?.map((vendor) => ({
            ...vendor,
            regularPercentage: vendor?.regularPercentage !== undefined
              ? Number(Number(vendor?.regularPercentage).toFixed(2))
              : vendor?.regularPercentage,
            hybridPercentage: vendor?.hybridPercentage !== undefined
              ? Number(Number(vendor?.hybridPercentage).toFixed(2))
              : vendor?.hybridPercentage,
            partialHybridPercentage: vendor?.partialHybridPercentage !== undefined
              ? Number(Number(vendor?.partialHybridPercentage).toFixed(2))
              : vendor?.partialHybridPercentage,
            remotePercentage: vendor?.remotePercentage !== undefined
              ? Number(Number(vendor?.remotePercentage).toFixed(2))
              : vendor?.remotePercentage,
            }));
          this.totalCount = this.vendorEmployeeData?.length;
          this.displayData = this.vendorEmployeeData || [];

          this.isDataLoaded = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.errorMsg = 'Failed to load vendor data.';
          this.isDataLoaded = true;
          this.displayData = [];
          this.totalCount = 0;
          this.cdr.detectChanges();
        },
      });
  }

  searchVendors(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event);
    this.displayData = this.vendorEmployeeData?.length
      ? this.searchService.getSearchedData(
          this.vendorEmployeeData,
          this.filterValue?.toLowerCase(),
        )
      : [];
    this.totalCount = this.displayData?.length;
  }

  clearInput() {
    this.displayData = this.vendorEmployeeData;
    this.totalCount = this.vendorEmployeeData?.length;
    this.filterValue = '';
  }

  onMonthChange(event: any): void {
    let selectedValue: string | undefined;
    if (typeof event === 'string') {
      const monthOption = this.monthOptions.find(
        (option) => option.label === event,
      );
      selectedValue = monthOption ? monthOption.value : undefined;
    } else if (event.value) {
      selectedValue = event.value;
    }
    if (
      !selectedValue ||
      !this.monthOptions.some((option) => option.value === selectedValue)
    ) {
      console.error('Invalid month selected.');
      return;
    }

    const selectedOption = this.monthOptions.find(
      (option) => option.value === selectedValue,
    );
    if (selectedOption) {
      this.selectedMonth = selectedOption.month;
      this.selectedYear = selectedOption.year;
      this.loadVendorData();
    }
  }

  navigateToVendorProfile(vendorId: string, vendorName: string) {
    if (!vendorId || !vendorName) {
      console.error('Invalid vendor details:', { vendorId, vendorName });
      return;
    }
    // Set local state for showing vendor profile inline (if used by template)
    this.showVendorProfile = true;
    // Attempt to find and set the selected vendor from loaded data
    const matchedVendor = this.vendorEmployeeData.find(
      (v) => v.vendorId?.toString() === vendorId?.toString(),
    );
    this.selectedVendor = matchedVendor ?? null;
    const encodedVendorName = encodeURIComponent(vendorName);
  }

  navigateToContractorList(vendorId: number, type: string) {
    if (!vendorId || !type) {
      this.toastr.error(RTODrill.URLError);
      return;
    }
    this.router.navigate(['/vendor-management/rto-drill'], {
      queryParams: {
        vendorId: vendorId,
        type: type,
        month: this.selectedMonth,
        year: this.selectedYear,
        employeeId : this.employeeId,
      },
    });
  }

  navigateToDashboard() {
    this.router.navigate(['/vendor-management']);
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
